import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { filesAPI } from '../services/api';

const MAX_CONCURRENT = 3;

let nextId = 0;

/**
 * Upload queue with parallel uploads (max 3 concurrent).
 * Each item has: { id, file, folderId, status, progress, error, abortController }
 * status: 'pending' | 'uploading' | 'done' | 'error'
 */
export function useUploadQueue() {
  const [items, setItems] = useState([]);
  const queueRef = useRef([]);
  const activeCount = useRef(0);
  const qc = useQueryClient();

  const updateItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const processNext = useCallback(() => {
    while (activeCount.current < MAX_CONCURRENT) {
      const next = queueRef.current.find((item) => item.status === 'pending');
      if (!next) break;

      activeCount.current++;
      next.status = 'uploading';
      updateItem(next.id, { status: 'uploading' });

      const abortController = new AbortController();
      next.abortController = abortController;

      const formData = new FormData();
      formData.append('file', next.file);
      if (next.folderId) formData.append('folder', next.folderId);

      filesAPI
        .upload(formData, {
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded * 100) / (e.total || 1));
            updateItem(next.id, { progress: pct });
          },
          signal: abortController.signal,
        })
        .then(() => {
          next.status = 'done';
          updateItem(next.id, { status: 'done', progress: 100 });
          qc.invalidateQueries({ queryKey: ['files'] });
          qc.invalidateQueries({ queryKey: ['folders'] });
        })
        .catch((err) => {
          if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
            next.status = 'error';
            updateItem(next.id, { status: 'error', error: 'Annullato' });
          } else {
            next.status = 'error';
            updateItem(next.id, { status: 'error', error: err.response?.data?.detail || 'Errore upload' });
          }
        })
        .finally(() => {
          activeCount.current--;
          processNext();
        });
    }
  }, [updateItem, qc]);

  const addFiles = useCallback(
    (fileList, folderId) => {
      const newItems = Array.from(fileList).map((file) => ({
        id: ++nextId,
        file,
        folderId,
        status: 'pending',
        progress: 0,
        error: null,
        abortController: null,
      }));

      queueRef.current.push(...newItems);
      setItems((prev) => [...prev, ...newItems.map(({ id, file, status, progress, error }) => ({
        id,
        name: file.name,
        size: file.size,
        status,
        progress,
        error,
      }))]);

      // Kick off processing
      processNext();
    },
    [processNext]
  );

  const cancelItem = useCallback((id) => {
    const item = queueRef.current.find((i) => i.id === id);
    if (item?.abortController) {
      item.abortController.abort();
    }
    if (item && item.status === 'pending') {
      item.status = 'error';
      updateItem(id, { status: 'error', error: 'Annullato' });
    }
  }, [updateItem]);

  const clearCompleted = useCallback(() => {
    queueRef.current = queueRef.current.filter((i) => i.status !== 'done' && i.status !== 'error');
    setItems((prev) => prev.filter((i) => i.status !== 'done' && i.status !== 'error'));
  }, []);

  const clearAll = useCallback(() => {
    // Abort all pending/uploading
    queueRef.current.forEach((item) => {
      if (item.abortController) item.abortController.abort();
    });
    queueRef.current = [];
    setItems([]);
    activeCount.current = 0;
  }, []);

  const hasActive = items.some((i) => i.status === 'pending' || i.status === 'uploading');

  const globalProgress = items.length > 0
    ? Math.round(items.reduce((sum, i) => sum + i.progress, 0) / items.length)
    : 0;

  return {
    items,
    addFiles,
    cancelItem,
    clearCompleted,
    clearAll,
    hasActive,
    globalProgress,
  };
}
